import { a as isEnvironmentLocal, c as isEnvironmentStaging, s as isEnvironmentProduction } from "./Environment-B1ZpdxIS.js";
import { useCallback, useEffect, useRef } from "react";
import { jsx } from "react/jsx-runtime";
import { http } from "msw";
//#region src/Utils/isDefined.ts
function isDefined(arg) {
	return arg != null;
}
//#endregion
//#region src/Utils/getMarketingUrl.ts
/**
* @param path on the marketing site, like `privacy-policy`
* @returns a link to the marketing host + path.
*/
function getMarketingUrl(path = "") {
	const pathWithPrefix = path.startsWith("/") ? path : `/${path}`;
	if (isEnvironmentLocal()) return `https://test-color-genomics.pantheonsite.io${pathWithPrefix}`;
	if (isEnvironmentStaging()) return `https://test-color-genomics.pantheonsite.io${pathWithPrefix}`;
	if (isEnvironmentProduction()) return `https://www.color.com${pathWithPrefix}`;
	return `https://test-color-genomics.pantheonsite.io${pathWithPrefix}`;
}
//#endregion
//#region src/Utils/withDefaultProps.tsx
/**
* Returns a modified version of the component with its props preset to the
* given default values.
*
* This is primarily useful for customizing wrappers for testing-library render()
* calls, since initialProps are not supplied to wrappers in testing-library v16.
*
* @param Component
* @param defaultProps
* @returns A new component with the default props applied
*/
function withDefaultProps(Component, defaultProps) {
	return (props) => /* @__PURE__ */ jsx(Component, {
		...defaultProps,
		...props
	});
}
//#endregion
//#region src/Utils/mswUtils.ts
/**
* Creates an MSW handler for the given endpoint and options.
*
* NOTE: We log API errors to the console by default, which will cause tests to fail. If using
* this in tests with an error status code, you can mock the console to avoid the error, e.g.:
* @example
* ```ts
* const consoleErrorSpy = vi.spyOn(console, 'error').mockImplementation(() => {});
* < code that triggers the API call >
* consoleErrorSpy.mockRestore();
* ```
*/
var createMswHandler = (requestHandler, endpoint, options) => {
	const { statusCode = 200, response = {} } = options || {};
	return requestHandler(endpoint, () => {
		return new Response(response ? JSON.stringify(response) : null, {
			status: statusCode,
			headers: { "Content-Type": "application/json" }
		});
	});
};
/**
* Creates a MSW GET handler for the given endpoint.
*/
var createMswGetHandler = (endpoint, options) => {
	return createMswHandler(http.get, endpoint, options);
};
/**
* Creates a MSW POST handler for the given endpoint.
*/
var createMswPostHandler = (endpoint, options) => {
	return createMswHandler(http.post, endpoint, options);
};
/**
* Creates a MSW PATCH handler for the given endpoint.
*/
var createMswPatchHandler = (endpoint, options) => {
	return createMswHandler(http.patch, endpoint, options);
};
/**
* Variation on `createMswHandler` that also returns functions that can be called to determine
* if a request happened, and inspect what the body of the request was.
*/
var createMswHandlerSpy = (requestHandler, endpoint, options) => {
	const { statusCode = 200, response = {} } = options || {};
	let requestQueryParams;
	let requestJsonData;
	let wasCalled = false;
	return {
		getRequestQueryParams: () => requestQueryParams,
		getRequestJsonData: () => requestJsonData,
		getWasCalled: () => wasCalled,
		handler: requestHandler(endpoint, async ({ request }) => {
			requestQueryParams = new URL(request.url).searchParams;
			try {
				requestJsonData = await request.json();
			} catch {} finally {
				wasCalled = true;
			}
			return new Response(response ? JSON.stringify(response) : null, {
				status: statusCode,
				headers: { "Content-Type": "application/json" }
			});
		})
	};
};
/**
* Creates a MSW GET handler that allows inspecting query parameters.
*/
var createMswGetHandlerSpy = (endpoint, options) => {
	const { getRequestQueryParams, getWasCalled, handler } = createMswHandlerSpy(http.get, endpoint, options);
	return {
		getRequestQueryParams,
		getWasCalled,
		handler
	};
};
/**
* Creates a MSW POST handler that allows inspecting the request body.
*/
var createMswPostHandlerSpy = (endpoint, options) => {
	const { getRequestJsonData, getWasCalled, handler } = createMswHandlerSpy(http.post, endpoint, options);
	return {
		getPostedValues: getRequestJsonData,
		getWasCalled,
		handler
	};
};
/**
* Creates a MSW PUT handler that allows inspecting the request body.
*/
var createMswPutHandlerSpy = (endpoint, options) => {
	const { getRequestJsonData, getWasCalled, handler } = createMswHandlerSpy(http.put, endpoint, options);
	return {
		getPutValues: getRequestJsonData,
		getWasCalled,
		handler
	};
};
//#endregion
//#region src/Utils/useDebounceFn.ts
function useDebounceFn(fn, delay) {
	const timeoutRef = useRef(null);
	useEffect(() => {
		return () => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
		};
	}, [fn, delay]);
	return useCallback((args) => {
		timeoutRef.current = setTimeout(() => fn(args), delay);
	}, [fn, delay]);
}
//#endregion
export { createMswPostHandler as a, withDefaultProps as c, createMswPatchHandler as i, getMarketingUrl as l, createMswGetHandler as n, createMswPostHandlerSpy as o, createMswGetHandlerSpy as r, createMswPutHandlerSpy as s, useDebounceFn as t, isDefined as u };

//# sourceMappingURL=Utils-B1sWMmqZ.js.map